import { CheckCircle2, AlertCircle, XCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import type { CityVerdict } from "./types";

interface VerdictControlProps {
  token: string;
  pilId: string;
  current: CityVerdict;
  disabled?: boolean;
}

const OPTIONS: Array<{
  value: Exclude<CityVerdict, null>;
  label: string;
  Icon: typeof CheckCircle2;
  activeCls: string;
  iconActiveCls: string;
  idleCls: string;
}> = [
  {
    value: "approve",
    label: "Approve",
    Icon: CheckCircle2,
    activeCls:
      "bg-green-600 border-green-600 text-white hover:bg-green-600/90",
    iconActiveCls: "text-white",
    idleCls: "border-green-600 text-green-700 hover:bg-green-50",
  },
  {
    value: "needs_change",
    label: "Needs change",
    Icon: AlertCircle,
    activeCls:
      "bg-amber-500 border-amber-500 text-white hover:bg-amber-500/90",
    iconActiveCls: "text-white",
    idleCls: "border-amber-500 text-amber-700 hover:bg-amber-50",
  },
  {
    value: "reject",
    label: "Reject",
    Icon: XCircle,
    activeCls:
      "bg-red-600 border-red-600 text-white hover:bg-red-600/90",
    iconActiveCls: "text-white",
    idleCls: "border-red-600 text-red-700 hover:bg-red-50",
  },
];

export function VerdictControl({
  token,
  pilId,
  current,
  disabled,
}: VerdictControlProps) {
  const utils = trpc.useUtils();
  const onSettle = () => {
    utils.cityReport.getReport.invalidate({ token });
  };
  const submit = trpc.cityReport.submitVerdict.useMutation({
    onSettled: onSettle,
  });
  const clear = trpc.cityReport.clearVerdict.useMutation({
    onSettled: onSettle,
  });

  const mutating = submit.isPending || clear.isPending;

  const handleClick = (value: Exclude<CityVerdict, null>) => {
    if (disabled || mutating) return;
    if (current === value) {
      clear.mutate({ token, pilId });
    } else {
      submit.mutate({ token, pilId, verdict: value });
    }
  };

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Your verdict
      </div>
      <div className="grid grid-cols-3 gap-2">
        {OPTIONS.map((opt) => {
          const isActive = current === opt.value;
          return (
            <button
              type="button"
              key={opt.value}
              onClick={() => handleClick(opt.value)}
              disabled={disabled || mutating}
              className={cn(
                "flex flex-col items-center justify-center gap-1 rounded-md border px-3 py-3 text-xs font-medium transition-colors cursor-pointer",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                isActive
                  ? opt.activeCls
                  : cn("bg-background", opt.idleCls),
              )}
              aria-pressed={isActive}
            >
              <opt.Icon
                className={cn("size-5", isActive && opt.iconActiveCls)}
              />
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
      {current && !disabled && (
        <p className="text-xs text-muted-foreground">
          Click the selected button again to clear your verdict.
        </p>
      )}
    </div>
  );
}
